import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { Trans } from '@lingui/react/macro';

import { PageLayout } from '@/components/PageLayout';
import Drawer from '@/design_system/Drawer';
import { getArticlePhoto } from '@/models/article';
import { useDeleteDraftRequestArticle } from '@/models/request';
import {
  ArticleContextProvider,
  useArticleContext,
} from '@/routes/Requests/contexts/ArticleContext';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { useScrollIntoView } from '@/utils/useScrollIntoView';

import { MultiStepCreationContextProvider } from '../contexts/MultiStepCreationContext';

import { NewArticleBody } from './components/NewArticleBody/NewArticleBody';
import { NewArticleFooter } from './components/NewArticleFooter';
import NewArticleHeader from './components/NewArticleHeader';

const NewArticle = () => {
  const { id, articleId } = useParams();
  const { request } = useRequestContext();
  const { state, pathname } = useLocation();
  const navigate = useNavigate();

  const article = request.articles.find(({ id }) => id === articleId);

  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    if (state?.from) {
      // Wait for the Drawer CSS enter animation to finish before resetting state
      setTimeout(
        () =>
          navigate(pathname, {
            state: { ...state, from: undefined, skipRouterBlocker: undefined },
            replace: true,
          }),
        150
      );
    }
  }, [navigate, pathname, state]);

  const articleIsEmpty =
    !!article &&
    !article.productId &&
    !article.productAttributeValues.length &&
    !article.color &&
    !article.size &&
    !article.purchaseDate &&
    !article.activeWarrantyType &&
    !article.snapshot.articleActions.length &&
    !getArticlePhoto(article);

  const { mutate: deleteDraftRequestArticle } = useDeleteDraftRequestArticle(request.id);

  const deleteAndClose = () => {
    if (article) {
      deleteDraftRequestArticle(article.id);
    }

    close();
  };

  const close = () => {
    setIsOpen(false);

    // Wait for the Drawer CSS exit animation to finish before navigating away
    setTimeout(
      () => navigate(`/requests/new/${id}`, { state: { skipRouterBlocker: true }, replace: true }),
      150
    );
  };

  const isEditing = !state?.isNew;

  return (
    <Drawer
      isOpen={isOpen}
      animateEntry={state?.from === 'request'}
      onOpenChange={isEditing ? close : deleteAndClose}
      closeConfirmation={
        articleIsEmpty || isEditing
          ? undefined
          : {
              title: <Trans id="dialog.discard-changes.title">Discard changes</Trans>,
              content: (
                <Trans id="dialog.discard-changes.content">
                  You're about to leave this page without saving your changes. All unsaved changes
                  will be lost.
                  <br />
                  Are you sure?
                </Trans>
              ),
              confirm: <Trans id="dialog.discard-changes.confirm">Discard changes</Trans>,
            }
      }
    >
      {!request || !article ? null : (
        <ArticleContextProvider article={article}>
          <MultiStepCreationContextProvider>
            <DrawerContent isEditing={isEditing} close={close} />
          </MultiStepCreationContextProvider>
        </ArticleContextProvider>
      )}
    </Drawer>
  );
};

const DrawerContent = ({ isEditing, close }: { isEditing: boolean; close: () => void }) => {
  const { request } = useRequestContext();
  const { article } = useArticleContext();

  const [alertRef, scrollAlertIntoView] = useScrollIntoView<HTMLDivElement>();

  return (
    <PageLayout>
      <NewArticleHeader request={request} article={article} isEditing={isEditing} />
      <NewArticleBody alertRef={alertRef} />
      <NewArticleFooter
        isEditing={isEditing}
        close={close}
        scrollAlertIntoView={scrollAlertIntoView}
      />
    </PageLayout>
  );
};

export default NewArticle;
